/**
 * ExcelArticleImport.tsx
 *
 * Componente per l'importazione massiva di articoli da file Excel.
 * Parsing lato frontend con libreria xlsx. Supporta anteprima, validazione
 * e download di un template vuoto con le colonne corrette.
 */

import React, { useRef, useState } from 'react';
import {
  Stack,
  Group,
  Button,
  Table,
  Text,
  Alert,
  Badge,
  Card,
  ScrollArea
} from '@mantine/core';
import {
  IconUpload,
  IconDownload,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconFileSpreadsheet
} from '@tabler/icons-react';
import * as XLSX from 'xlsx';

/** Colonne attese nel file Excel */
const EXPECTED_COLUMNS = [
  'Dimensioni',
  'Numero Articolo',
  'Configurazione',
  'Decoro',
  'Nome prodotto',
  'Quantita',
  'Note'
] as const;

interface ImportedArticle {
  articleCode: string;
  articleName: string;
  quantity: number;
  notes?: string;
}

interface ParsedRow {
  dimensioni: string;
  numeroArticolo: string;
  configurazione: string;
  decoro: string;
  nomeProdotto: string;
  nome: string;
  quantita: number;
  note: string;
  /** SKU calcolato = CONCAT(Numero Articolo, Configurazione, Dimensioni, Decoro) senza separatori */
  sku: string;
  /** Nome articolo finale = concatenazione di nomeProdotto e nome */
  articleName: string;
  /** Errori di validazione per questa riga */
  errors: string[];
}

interface ExcelArticleImportProps {
  onImport: (articles: ImportedArticle[]) => void;
}

export function ExcelArticleImport({ onImport }: ExcelArticleImportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  /** Normalizza il nome colonna per il matching (lowercase, trim, senza accenti) */
  const normalizeHeader = (header: string): string =>
    header
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  /** Mappa header normalizzati ai nomi attesi */
  const HEADER_MAP: Record<string, keyof Omit<ParsedRow, 'sku' | 'articleName' | 'errors'>> = {
    'dimensioni': 'dimensioni',
    'numero articolo': 'numeroArticolo',
    'configurazione': 'configurazione',
    'decoro': 'decoro',
    'nome prodotto': 'nomeProdotto',
    'nome': 'nome',
    'quantita': 'quantita',
    'note': 'note'
  };

  /** Gestisce il caricamento del file */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setParsedRows([]);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellText: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonRows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '', raw: false });

        if (jsonRows.length === 0) {
          setParseError('Il file Excel è vuoto o non contiene dati.');
          return;
        }

        // Mappa le colonne del file ai campi attesi
        const fileHeaders = Object.keys(jsonRows[0]);
        const columnMap: Record<string, string> = {};

        for (const fileHeader of fileHeaders) {
          const normalized = normalizeHeader(fileHeader);
          const mappedKey = HEADER_MAP[normalized];
          if (mappedKey) {
            columnMap[fileHeader] = mappedKey;
          }
        }

        // Verifica colonne obbligatorie
        const mappedFields = new Set(Object.values(columnMap));
        const requiredFields = ['numeroArticolo', 'nomeProdotto'];
        const missingRequired = requiredFields.filter(f => !mappedFields.has(f));

        if (missingRequired.length > 0) {
          setParseError(
            `Colonne obbligatorie mancanti: ${missingRequired.map(f =>
              f === 'numeroArticolo' ? 'Numero Articolo' : 'Nome prodotto'
            ).join(', ')}. Colonne trovate: ${fileHeaders.join(', ')}`
          );
          return;
        }

        // Parse delle righe
        const rows: ParsedRow[] = jsonRows.map((row) => {
          const getValue = (field: string): string => {
            const fileCol = Object.entries(columnMap).find(([, v]) => v === field)?.[0];
            return fileCol ? String(row[fileCol] ?? '').trim() : '';
          };

          const dimensioni = getValue('dimensioni');
          const numeroArticolo = getValue('numeroArticolo');
          const configurazione = getValue('configurazione');
          const decoro = getValue('decoro');
          const nomeProdotto = getValue('nomeProdotto');
          const nome = getValue('nome');
          const rawQuantita = getValue('quantita');
          const note = getValue('note');

          // Calcola SKU: concatenazione senza separatori, preservando i valori come sono (es. 01, 0100)
          const skuParts = [numeroArticolo, configurazione, dimensioni, decoro].filter(Boolean);
          const sku = skuParts.join('');

          // Nome articolo finale: concatena nomeProdotto e nome (se entrambi presenti)
          const articleName = [nomeProdotto, nome].filter(Boolean).join(' ');

          // Parse quantità
          const quantita = parseInt(rawQuantita, 10);
          const parsedQuantita = isNaN(quantita) || quantita < 1 ? 1 : quantita;

          // Validazione
          const errors: string[] = [];
          if (!numeroArticolo) errors.push('Numero Articolo mancante');
          if (!nomeProdotto) errors.push('Nome prodotto mancante');

          return {
            dimensioni,
            numeroArticolo,
            configurazione,
            decoro,
            nomeProdotto,
            nome,
            quantita: parsedQuantita,
            note,
            sku,
            articleName,
            errors
          };
        });

        // Filtra righe completamente vuote
        const nonEmptyRows = rows.filter(r =>
          r.numeroArticolo || r.nomeProdotto || r.nome || r.dimensioni || r.configurazione || r.decoro
        );

        if (nonEmptyRows.length === 0) {
          setParseError('Nessuna riga valida trovata nel file.');
          return;
        }

        setParsedRows(nonEmptyRows);
      } catch {
        setParseError('Errore durante la lettura del file Excel. Verificare il formato.');
      }
    };
    reader.readAsArrayBuffer(file);

    // Reset input per permettere di ricaricare lo stesso file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /** Conferma l'import e chiama il callback */
  const handleConfirmImport = () => {
    const validRows = parsedRows.filter(r => r.errors.length === 0);
    const articles: ImportedArticle[] = validRows.map(r => ({
      articleCode: r.sku,
      articleName: r.articleName,
      quantity: r.quantita,
      ...(r.note ? { notes: r.note } : {})
    }));

    onImport(articles);
    // Reset stato
    setParsedRows([]);
    setFileName(null);
    setParseError(null);
  };

  /** Scarica un template Excel vuoto */
  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      [...EXPECTED_COLUMNS],
      // Riga di esempio
      ['20cm', 'ART001', 'CFG-A', 'DEC-01', 'Vaso Decorativo', '10', 'Esempio nota']
    ]);

    // Imposta larghezza colonne
    ws['!cols'] = [
      { wch: 14 }, // Dimensioni
      { wch: 18 }, // Numero Articolo
      { wch: 18 }, // Configurazione
      { wch: 14 }, // Decoro
      { wch: 25 }, // Nome prodotto
      { wch: 10 }, // Quantita
      { wch: 25 }  // Note
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Articoli');
    XLSX.writeFile(wb, 'template_articoli.xlsx');
  };

  /** Annulla l'anteprima */
  const handleCancel = () => {
    setParsedRows([]);
    setFileName(null);
    setParseError(null);
  };

  const validCount = parsedRows.filter(r => r.errors.length === 0).length;
  const errorCount = parsedRows.filter(r => r.errors.length > 0).length;

  return (
    <Stack gap="sm">
      {/* Pulsanti azione */}
      <Group gap="xs">
        <Button
          variant="light"
          size="xs"
          leftSection={<IconUpload size={14} />}
          onClick={() => fileInputRef.current?.click()}
        >
          Importa da Excel
        </Button>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconDownload size={14} />}
          onClick={handleDownloadTemplate}
        >
          Scarica Template
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Group>

      {/* Errore di parsing */}
      {parseError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setParseError(null)}>
          {parseError}
        </Alert>
      )}

      {/* Anteprima dati */}
      {parsedRows.length > 0 && (
        <Card withBorder padding="sm">
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <IconFileSpreadsheet size={16} />
                <Text size="sm" fw={500}>{fileName}</Text>
              </Group>
              <Group gap="xs">
                <Badge color="green" size="sm" variant="light">{validCount} validi</Badge>
                {errorCount > 0 && (
                  <Badge color="red" size="sm" variant="light">{errorCount} con errori</Badge>
                )}
              </Group>
            </Group>

            <ScrollArea h={Math.min(parsedRows.length * 42 + 40, 250)}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th></Table.Th>
                    <Table.Th>SKU</Table.Th>
                    <Table.Th>Nome prodotto</Table.Th>
                    <Table.Th>Quantita</Table.Th>
                    <Table.Th>Note</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {parsedRows.map((row, i) => (
                    <Table.Tr key={i} bg={row.errors.length > 0 ? 'red.0' : undefined}>
                      <Table.Td>
                        {row.errors.length > 0 ? (
                          <IconAlertCircle size={14} color="red" />
                        ) : (
                          <IconCheck size={14} color="green" />
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">{row.sku || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{row.articleName || '-'}</Text>
                        {row.errors.length > 0 && (
                          <Text size="xs" c="red">{row.errors.join(', ')}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{row.quantita}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" lineClamp={1}>{row.note || '-'}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" size="xs" onClick={handleCancel} leftSection={<IconX size={14} />}>
                Annulla
              </Button>
              <Button
                size="xs"
                onClick={handleConfirmImport}
                disabled={validCount === 0}
                leftSection={<IconCheck size={14} />}
              >
                Importa {validCount} articol{validCount !== 1 ? 'i' : 'o'}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
